/**
 * Bringing a model of one's own into the 3D library
 * (docs/17-asset-library/10-imports.md §2, §8, §10): the ticket, the PUT
 * straight to the blob store, the confirmation, the list of what waits and
 * one import's detail. The API never sees the file.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ASSETLIB_KEY, type Saved } from "./assetlib";
import { api, newIdempotencyKey } from "./client";
import { MEDIA_KEY } from "./media";
import { ME_KEY } from "./me";
import type {
  ImportDetail,
  ImportLicenceChoice,
  ImportList,
  ImportPatch,
  ImportState,
  ImportTicket,
} from "./types";

export const IMPORTS_KEY = ["assetlib", "imports"] as const;
export const IMPORT_KEY = (id: string) => ["assetlib", "imports", id] as const;
/** The largest model file (`IMPORT_MAX_BYTES`): 100 MB. */
export const IMPORT_MAX_BYTES = 104_857_600;
/** The largest archive of a model with its textures (`IMPORT_ZIP_MAX_BYTES`):
 *  300 MB. */
export const IMPORT_ZIP_MAX_BYTES = 314_572_800;
/** The file formats accepted, as the extension of the file's name. */
export const IMPORT_EXTENSIONS = ["glb", "fbx", "obj", "zip"] as const;
/** The largest file of a format the import accepts, by its extension. */
export function importMaxBytes(extension: string): number {
  return extension === "zip" ? IMPORT_ZIP_MAX_BYTES : IMPORT_MAX_BYTES;
}

/** An import that is still on its way by itself: the page asks again. */
const moving = (state: ImportState) =>
  state === "uploaded" || state === "checking";

/** `GET /v1/assetlib/imports`: the organisation's models that wait for
 *  something, newest change first. While one is not uploaded yet or is being
 *  checked, the list is asked for again every 10 s. */
export function useImports(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: IMPORTS_KEY,
    queryFn: () => api<ImportList>("/v1/assetlib/imports"),
    enabled: options.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => moving(item.state))
        ? 10_000
        : false,
  });
}

/** `GET /v1/assetlib/imports/{id}`, asked again every 2 s while the model is
 *  not uploaded yet or is being checked. */
export function useImport(id: string) {
  return useQuery({
    queryKey: IMPORT_KEY(id),
    queryFn: () =>
      api<ImportDetail>(`/v1/assetlib/imports/${encodeURIComponent(id)}`),
    refetchInterval: (query) =>
      query.state.data && moving(query.state.data.state) ? 2_000 : false,
  });
}

/** After a write: the lists and details of imports (they live under the 3D
 *  library's key), the objects found in an ad (each is an import), the grid,
 *  the files page's figures and the header's. */
function useAfterWrite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: IMPORTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ASSETLIB_KEY });
    void queryClient.invalidateQueries({ queryKey: MEDIA_KEY });
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  };
}

/** The transfer of the file did not finish: it broke, or the user stopped
 *  it. Either way the import was discarded. */
export class ImportUploadError extends Error {
  constructor(public readonly code: "put_failed" | "cancelled") {
    super(code);
    this.name = "ImportUploadError";
  }
}

export interface NewImport {
  file: File;
  /** The name with the file's extension, as the row stores it. */
  name: string;
  license: ImportLicenceChoice;
}

/** The presigned PUT. The body is the `File` itself, never read into memory
 *  first: a model may be hundreds of megabytes. Cross-origin to the blob
 *  store, so no cookie, and no header but the content type the signature
 *  covers. */
function putFile(
  url: string,
  file: File,
  signal: AbortSignal,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new ImportUploadError("cancelled"));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.withCredentials = false;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ImportUploadError("put_failed"));
    };
    xhr.onerror = () => reject(new ImportUploadError("put_failed"));
    xhr.onabort = () => reject(new ImportUploadError("cancelled"));
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

const discard = (id: string) =>
  api<void>(`/v1/assetlib/imports/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: {},
  });

/** The ticket, the PUT, then the confirmation; resolves to the import's id.
 *  A PUT that fails or is stopped discards the import (which frees its place
 *  in the storage at once) and rejects with an `ImportUploadError`. */
export async function createImport(
  input: NewImport,
  signal: AbortSignal,
  onProgress: (fraction: number) => void,
): Promise<string> {
  const ticket = await api<ImportTicket>("/v1/assetlib/imports", {
    method: "POST",
    body: {
      name: input.name,
      bytes: input.file.size,
      license: input.license,
    },
    idempotencyKey: newIdempotencyKey(),
  });
  try {
    await putFile(ticket.put_url, input.file, signal, onProgress);
  } catch (error) {
    // Best effort: an import that could not be discarded now expires later.
    await discard(ticket.import_id).catch(() => undefined);
    throw error;
  }
  await api<{ state: ImportState }>(
    `/v1/assetlib/imports/${encodeURIComponent(ticket.import_id)}/uploaded`,
    { method: "POST", body: {} },
  );
  return ticket.import_id;
}

/** `POST /v1/assetlib/imports/{id}/uploaded` by itself: the page of a model
 *  that is still "not uploaded" says once that the file may have arrived (the
 *  tab was closed between the transfer and this call). */
export const confirmUploaded = (id: string) =>
  api<{ state: ImportState }>(
    `/v1/assetlib/imports/${encodeURIComponent(id)}/uploaded`,
    { method: "POST", body: {} },
  );

/** The upload of one model, as a mutation with its progress (0 to 1) and a
 *  way to stop it. `error` is an `ApiError` (the ticket or the confirmation
 *  was refused) or an `ImportUploadError`. */
export function useCreateImport() {
  const after = useAfterWrite();
  const [progress, setProgress] = useState(0);
  const controller = useRef<AbortController | null>(null);
  const mutation = useMutation({
    mutationFn: (input: NewImport) => {
      controller.current = new AbortController();
      setProgress(0);
      return createImport(input, controller.current.signal, setProgress);
    },
    onSettled: after,
  });
  return {
    ...mutation,
    progress,
    /** Stops the transfer; the mutation then fails as `cancelled`. */
    cancel: () => controller.current?.abort(),
  };
}

/** `PATCH /v1/assetlib/imports/{id}`: the details the member changed, all at
 *  once. `review` when nothing has to be checked again, else `checking`. */
export function usePatchImport(id: string) {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (changes: ImportPatch) =>
      api<{ state: ImportState }>(
        `/v1/assetlib/imports/${encodeURIComponent(id)}`,
        { method: "PATCH", body: changes },
      ),
    onSuccess: after,
  });
}

/** `POST /v1/assetlib/imports/{id}/publish`: the model becomes an object of
 *  the organisation's 3D library. */
export function usePublishImport(id: string) {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: () =>
      api<Saved<"shape">>(
        `/v1/assetlib/imports/${encodeURIComponent(id)}/publish`,
        { method: "POST", body: {}, idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: after,
  });
}

/** `POST /v1/assetlib/imports/{id}/reject`: discard. */
export function useRejectImport(id: string) {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: () => discard(id),
    onSuccess: after,
  });
}
